import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExpenseRequestService } from '../services/expenseRequestService';
import type { ExpenseRequest } from '../types/expenseRequest';

const ExpenseRequestDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expenseRequest, setExpenseRequest] = useState<ExpenseRequest | null>(null);

  useEffect(() => {
    document.title = 'Expense Request Detail';
  }, []);

  useEffect(() => {
    if (!id) {
      setError('Expense request ID is required');
      setLoading(false);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const req = await ExpenseRequestService.getExpenseRequestById(id);
        if (!isMounted) return;
        setExpenseRequest(req);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load expense request');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const formatDate = (iso: string | undefined | null | { $date?: string }) => {
    if (!iso) return '—';
    let dateStr: string;
    if (typeof iso === 'object' && (iso as any).$date) {
      dateStr = String((iso as any).$date);
    } else if (typeof iso === 'string') {
      dateStr = iso;
    } else {
      return '—';
    }
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };

  const formatValue = (value: any): string => {
    if (value == null) return '—';
    if (typeof value === 'string') return value || '—';
    if (typeof value === 'number') return value.toLocaleString('en-IN');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      if (value.$date) return formatDate(value);
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const data = useMemo(() => {
    const d: any = (expenseRequest as any)?.data;
    return d && typeof d === 'object' ? d : null;
  }, [expenseRequest]);

  const user = useMemo(() => {
    const u: any = (expenseRequest as any)?.userId ?? (expenseRequest as any)?.user;
    return u && typeof u === 'object' ? u : null;
  }, [expenseRequest]);

  const summary = (() => {
    const amount = data?.amount ?? (expenseRequest as any)?.amount;
    const creditCard = data?.creditCard ?? (expenseRequest as any)?.creditCard;
    const type = data?.notes?.type ?? data?.type ?? (expenseRequest as any)?.type;
    const notes = data?.notes?.notes ?? data?.notes ?? (expenseRequest as any)?.notes;

    const email = user?.email ?? data?.email;
    const phone = user?.phone ?? user?.mobile ?? data?.phone ?? data?.mobile;

    return {
      email: typeof email === 'string' ? email : '—',
      phone: typeof phone === 'string' ? phone : '—',
      amount: typeof amount === 'number' ? amount.toLocaleString('en-IN') : typeof amount === 'string' ? amount : '—',
      type: typeof type === 'string' ? type : '—',
      creditCard: typeof creditCard === 'string' ? creditCard : '—',
      notes: typeof notes === 'string' ? notes : notes ? formatValue(notes) : '—',
    };
  })();

  const downloadAsText = () => {
    if (!expenseRequest) return;

    let content = 'EXPENSE REQUEST DETAILS\n';
    content += '='.repeat(50) + '\n\n';

    content += 'SUMMARY\n';
    content += '-'.repeat(50) + '\n';
    Object.entries(summary).forEach(([key, value]) => {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
      content += `${label}: ${formatValue(value)}\n`;
    });

    content += '\nREQUEST METADATA\n';
    content += '-'.repeat(50) + '\n';
    content += `Request ID: ${expenseRequest._id}\n`;
    content += `Created At: ${formatDate((expenseRequest as any).createdAt)}\n`;
    content += `Updated At: ${formatDate((expenseRequest as any).updatedAt)}\n\n`;

    content += 'RAW DATA\n';
    content += '-'.repeat(50) + '\n';
    content += data ? JSON.stringify(data, null, 2) : 'No data payload available';
    content += '\n';

    const safe = (v: string) => v.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `${safe(summary.email !== '—' ? summary.email : 'user')}-${safe(summary.type !== '—' ? summary.type : 'expense')}-${stamp}.txt`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && !expenseRequest) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading expense request...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Expense Request Detail</h1>
          <button
            onClick={() => navigate('/expenses/requests')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      </div>
    );
  }

  if (!expenseRequest) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Expense Request Detail</h1>
          <button
            onClick={() => navigate('/expenses/requests')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md p-4 text-sm">Not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expense Request Detail</h1>
          <p className="mt-1 text-sm text-gray-500">Request ID: {expenseRequest._id}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={downloadAsText}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Download as text
          </button>
          <button
            onClick={() => navigate('/expenses/requests')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h2 className="text-sm font-semibold text-gray-900">Summary</h2>
          <dl className="mt-3 space-y-2">
            <div>
              <dt className="text-xs text-gray-500">Email</dt>
              <dd className="text-sm text-gray-900 break-all">{summary.email}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Phone</dt>
              <dd className="text-sm text-gray-900">{summary.phone}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Amount</dt>
              <dd className="text-sm text-gray-900">{summary.amount}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Type</dt>
              <dd className="text-sm text-gray-900">{summary.type}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Credit Card</dt>
              <dd className="text-sm text-gray-900">{summary.creditCard}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Notes</dt>
              <dd className="text-sm text-gray-900 whitespace-pre-wrap break-words">{summary.notes}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Created At</dt>
              <dd className="text-sm text-gray-900">{formatDate((expenseRequest as any).createdAt)}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Updated At</dt>
              <dd className="text-sm text-gray-900">{formatDate((expenseRequest as any).updatedAt)}</dd>
            </div>
          </dl>
        </div>

        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <h2 className="text-sm font-semibold text-gray-900">Raw Payload</h2>
          <pre className="mt-3 text-xs bg-gray-50 border border-gray-200 rounded-md p-3 overflow-auto whitespace-pre-wrap">
            {data ? JSON.stringify(data, null, 2) : 'No data payload available'}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default ExpenseRequestDetail;
